import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { fetchProducts } from "@/lib/shopify";
import { Price } from "@/components/Price";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function SearchDialog({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  // Lazy-load products only once when first opened
  const { data: products } = useQuery({
    queryKey: ["search-all-products"],
    queryFn: () => fetchProducts(50),
    enabled: open,
  });

  const results = useMemo(() => {
    if (!products) return [];
    const q = query.toLowerCase().trim();
    if (!q) return products.slice(0, 8);
    return products
      .filter((p) =>
        p.node.title.toLowerCase().includes(q) ||
        p.node.description?.toLowerCase().includes(q) ||
        p.node.tags?.some((t) => t.toLowerCase().includes(q))
      )
      .slice(0, 8);
  }, [products, query]);

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 max-w-2xl bg-[#FDFAF3] border-[#D4CCBC]">
        <DialogTitle className="sr-only">Search products</DialogTitle>
        <Command className="bg-[#FDFAF3]" shouldFilter={false}>
          <CommandInput
            placeholder="Search hats, tops, styles…"
            value={query}
            onValueChange={setQuery}
            className="font-ui text-base text-[#2A1510] placeholder:text-[#8B6F47]"
          />
          <CommandList className="max-h-[420px]">
            <CommandEmpty>
              <span className="font-ui text-sm text-[#8B6F47]">No products found.</span>
            </CommandEmpty>
            {results.length > 0 && (
              <CommandGroup heading={query ? "Results" : "Popular"}>
                {results.map((p) => {
                  const img = p.node.images.edges[0]?.node;
                  const price = parseFloat(p.node.priceRange.minVariantPrice.amount);
                  return (
                    <CommandItem
                      key={p.node.id}
                      value={p.node.handle}
                      onSelect={() => {
                        navigate({ to: "/products/$handle", params: { handle: p.node.handle } });
                        onOpenChange(false);
                        setQuery("");
                      }}
                      className="flex cursor-pointer items-center gap-3 px-2 py-2 data-[selected=true]:bg-[#F5EFE2]"
                    >
                      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-sm bg-[#F5EFE2]">
                        {img && <img src={img.url} alt={img.altText || p.node.title} className="h-full w-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="truncate font-heading text-sm font-bold uppercase text-[#2A1510]">{p.node.title}</p>
                        <p className="font-ui text-[11px] uppercase tracking-wider text-[#8B6F47]">{p.node.productType || "Hat"}</p>
                      </div>
                      <Price amount={price} className="font-body text-sm font-bold text-[#2A1510]" />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
          <div className="border-t border-[#D4CCBC] px-3 py-2">
            <p className="font-ui text-[10px] uppercase tracking-wider text-[#8B6F47]">
              Press <kbd className="rounded border border-[#D4CCBC] px-1.5 py-0.5 font-mono text-[10px]">⌘ K</kbd> any time to search
            </p>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
